/**
 * DiagnosticEngine.js
 * Sandbox runner and test harness for DataTableWrapper diagnostic plugins.
 */
import { defaultDiagnosticRegistry } from './DiagnosticRegistry';

function resolveDataTableBuilder() {
    if (typeof window !== 'undefined' && window.DataTableBuilder) {
        return window.DataTableBuilder;
    }
    if (typeof globalThis !== 'undefined' && globalThis.DataTableBuilder) {
        return globalThis.DataTableBuilder;
    }
    return null;
}

export class DiagnosticEngine {
    /**
     * @param {import('./DiagnosticRegistry').DiagnosticRegistry} [registry]
     */
    constructor(registry = defaultDiagnosticRegistry) {
        this._registry = registry;
    }

    /**
     * Runs diagnostic checks against registered plugins or target element.
     * @param {Object|string|HTMLElement} [optionsOrTarget]
     * @returns {Promise<DiagnosticReport>}
     */
    async diagnose(optionsOrTarget = {}) {
        let options = {};
        if (typeof optionsOrTarget === 'string' || (typeof HTMLElement !== 'undefined' && optionsOrTarget instanceof HTMLElement)) {
            options = { target: optionsOrTarget };
        } else if (typeof optionsOrTarget === 'object' && optionsOrTarget !== null) {
            options = { ...optionsOrTarget };
        }

        const plugins = this._resolvePlugins(options.only);
        const startTime = Date.now();
        const results = [];

        for (const plugin of plugins) {
            const pluginStart = Date.now();
            let sandbox = null;
            try {
                sandbox = this._createSandbox(options.container);
                const context = this._buildContext(sandbox, options);
                const pluginResult = await plugin.run(context);

                results.push({
                    id: plugin.id,
                    name: plugin.name,
                    category: plugin.category,
                    passed: Boolean(pluginResult?.passed),
                    durationMs: Date.now() - pluginStart,
                    details: pluginResult?.details ?? (pluginResult?.passed ? 'Passed' : 'Failed'),
                    checks: Array.isArray(pluginResult?.checks) ? pluginResult.checks : [],
                    errors: Array.isArray(pluginResult?.errors) ? pluginResult.errors : [],
                    data: pluginResult?.data ?? null
                });
            } catch (err) {
                results.push({
                    id: plugin.id,
                    name: plugin.name,
                    category: plugin.category,
                    passed: false,
                    durationMs: Date.now() - pluginStart,
                    details: `Diagnostic execution error: ${err.message}`,
                    errors: [err.stack || err.message]
                });
            } finally {
                if (sandbox && !options.keepSandbox) {
                    sandbox.cleanup();
                }
            }
        }

        const totalPassed = results.filter(r => r.passed).length;
        const totalFailed = results.length - totalPassed;

        return {
            timestamp: new Date().toISOString(),
            passed: totalFailed === 0,
            summary: {
                total: results.length,
                passed: totalPassed,
                failed: totalFailed,
                durationMs: Date.now() - startTime
            },
            results
        };
    }

    _resolvePlugins(only) {
        const all = this._registry.getAll();
        if (!only) return all;
        const filterList = Array.isArray(only) ? only : [only];
        return all.filter(p => filterList.includes(p.id) || filterList.includes(p.category));
    }

    _createSandbox(customContainer) {
        if (typeof document === 'undefined') {
            return {
                container: null,
                cleanup: () => {}
            };
        }

        let container;
        let isCustom = false;

        if (customContainer) {
            container = typeof customContainer === 'string'
                ? document.querySelector(customContainer)
                : customContainer;
            isCustom = true;
        }

        if (!container) {
            container = document.createElement('div');
            container.className = 'dt-diagnostic-sandbox';
            container.setAttribute('aria-hidden', 'true');
            container.style.cssText = 'position: absolute; left: -9999px; top: 0; width: 1200px; opacity: 0; pointer-events: none;';
            document.body.appendChild(container);
        }

        const cleanup = () => {
            if (!isCustom && container && container.parentNode) {
                container.parentNode.removeChild(container);
            } else if (isCustom && container) {
                container.innerHTML = '';
            }
        };

        return { container, cleanup };
    }

    _buildContext(sandbox, options) {
        const self = this;
        let targetEl = null;

        if (options.target) {
            targetEl = typeof options.target === 'string'
                ? document.querySelector(options.target)
                : options.target;
        }

        // Resolve jQuery lazily — avoid ReferenceError in environments without jQuery
        function resolveJQuery() {
            if (typeof window !== 'undefined' && window.jQuery) return window.jQuery;
            if (typeof globalThis !== 'undefined' && globalThis.jQuery) return globalThis.jQuery;
            return null;
        }

        return {
            targetEl,
            container: sandbox.container,
            DataTableBuilder: resolveDataTableBuilder(),
            get $() { return resolveJQuery(); },

            /**
             * Returns true if DataTableBuilder and its jQuery dependency are available.
             * Plugins should check this before calling createTable().
             */
            canCreateTable() {
                return Boolean(resolveDataTableBuilder() && resolveJQuery());
            },

            /**
             * Mounts a real table instance using DataTableBuilder inside the sandbox.
             * Returns { tableEl, adapter, destroy } on success or { tableEl: null, skipped: true, destroy } when
             * the runtime environment lacks jQuery/DataTableBuilder (e.g. Vitest jsdom without a full browser bundle).
             * @param {Function} buildFn
             * @param {Object} [tableOptions]
             * @returns {Promise<{ tableEl: HTMLTableElement|null, adapter: any, destroy: Function, skipped?: boolean }>}
             */
            async createTable(buildFn, tableOptions = {}) {
                if (!sandbox.container) {
                    return { tableEl: null, adapter: null, destroy: () => {}, skipped: true, skipReason: 'No sandbox container available.' };
                }

                const Builder = resolveDataTableBuilder();
                if (!Builder || !resolveJQuery()) {
                    return {
                        tableEl: null,
                        adapter: null,
                        destroy: () => {},
                        skipped: true,
                        skipReason: 'DataTableBuilder or jQuery not available in this environment (headless/test mode).'
                    };
                }

                const tableEl = document.createElement('table');
                tableEl.className = `table dt-core ${tableOptions.className || ''}`;
                tableEl.style.width = tableOptions.width || '100%';
                sandbox.container.appendChild(tableEl);

                const builder = Builder.create(tableEl);
                if (typeof buildFn === 'function') {
                    buildFn(builder);
                }

                const adapter = builder.build();

                // Wait for DataTables DOM mount
                await new Promise(resolve => setTimeout(resolve, 50));

                const destroy = () => {
                    try {
                        const jq = resolveJQuery();
                        if (jq && jq.fn.DataTable && jq.fn.DataTable.isDataTable(tableEl)) {
                            jq(tableEl).DataTable().destroy();
                        }
                    } catch (e) { /* ignore */ }
                    if (tableEl.parentNode) {
                        tableEl.parentNode.removeChild(tableEl);
                    }
                };

                return { tableEl, adapter, destroy };
            },

            /**
             * Measures whether two DOM bounding rects overlap.
             * @param {Element|DOMRect} a
             * @param {Element|DOMRect} b
             * @param {number} [tolerance=0]
             * @returns {boolean}
             */
            measureCollision(a, b, tolerance = 0) {
                return self._isColliding(a, b, tolerance);
            },

            /**
             * Measures the bounding client rect of text inside an element (excluding child tags).
             * @param {HTMLElement} element
             * @returns {DOMRect|null}
             */
            measureTextRect(element) {
                return self._getTextBoundingRect(element);
            },

            /**
             * Inspects loaded stylesheets matching a regex pattern.
             * @param {RegExp} selectorPattern
             * @param {RegExp} [cssTextPattern]
             * @returns {CSSRule[]}
             */
            inspectCssRules(selectorPattern, cssTextPattern) {
                const matched = [];
                if (typeof document === 'undefined') return matched;
                try {
                    for (const sheet of document.styleSheets) {
                        try {
                            for (const rule of (sheet.cssRules || [])) {
                                if (rule.selectorText && selectorPattern.test(rule.selectorText)) {
                                    if (!cssTextPattern || cssTextPattern.test(rule.cssText)) {
                                        matched.push(rule);
                                    }
                                }
                            }
                        } catch (e) { /* skip cross-origin */ }
                    }
                } catch (e) { /* ignore */ }
                return matched;
            }
        };
    }

    _isColliding(a, b, tolerance = 0) {
        if (!a || !b) return false;
        const rectA = a instanceof DOMRect ? a : a.getBoundingClientRect();
        const rectB = b instanceof DOMRect ? b : b.getBoundingClientRect();

        // Check if either has zero width/height (not rendered)
        if (rectA.width === 0 || rectA.height === 0 || rectB.width === 0 || rectB.height === 0) {
            return false;
        }

        return !(
            rectA.right - tolerance <= rectB.left ||
            rectA.left + tolerance >= rectB.right ||
            rectA.bottom - tolerance <= rectB.top ||
            rectA.top + tolerance >= rectB.bottom
        );
    }

    _getTextBoundingRect(element) {
        if (!element || typeof document === 'undefined') return null;
        try {
            const range = document.createRange();
            // Find text nodes
            for (let i = 0; i < element.childNodes.length; i++) {
                const node = element.childNodes[i];
                if (node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0) {
                    range.selectNodeContents(node);
                    const rect = range.getBoundingClientRect();
                    if (rect.width > 0 && rect.height > 0) {
                        return rect;
                    }
                }
            }
        } catch (e) { /* fallback */ }
        return element.getBoundingClientRect();
    }
}
